import { useGet, usePost, usePut, useQueryWithErrorHandling } from '@app/sdk';
import { useQueryClient } from '@tanstack/react-query';
import { isAfter } from 'date-fns';
import { useAccountIdContext } from '../../components/account/AccountIdContext';

function useTransactionService() {
  const get = useGet(true, "ams");
  const post = usePost(true, "ams");
  const put = usePut(true, "ams");

  function getAccount(accountId: string) {
    return get<TransactionAccountAvailableCredit>(`/api/proxy/ams/transaction-service/account/${accountId}`);
  }

  function getPendingAuthorizations(accountId: string) {
    return get<PendingAuthorizations>(`/api/proxy/ams/transaction-service/account/${accountId}/auth`);
  }

  function payment({
    accountId,
    amount,
    description,
    effectiveDate,
    creditRelease = null,
  }: {
    accountId: string;
    amount: number;
    description: string;
    effectiveDate: Date;
    creditRelease?: string;
  }) {
    const body = {
      amount: {
        value: amount,
        currencyCode: 'GBP',
      },
      transactionDate: this._billingDate.toISOString(),
      effectiveDate: isAfter(this._billingDate, effectiveDate) ? effectiveDate : undefined,
      balanceMovementType: 'Payment',
      spendType: 'Purchase',
      creditRelease: creditRelease,
      description,
    };
    return post(`/api/proxy/ams/transaction-service/account/${accountId}/transaction`, body);
  }

  function reverseTransaction(accountId: string, transactionId: string, description: string) {
    const body = {
      description,
    };
    return post(`/api/proxy/ams/transaction-service/account/${accountId}/transaction/${transactionId}/reverse`, body);
  }

  function releasePayment(accountId: string, transactionId: string) {
    return post(`/api/proxy/ams/transaction-service/account/${accountId}/transaction/${transactionId}/release`, null);
  }

  function changeStatus(accountId: string, transactionId: string, newTransactionStatus) {
    const body = {
      transactionStatus: newTransactionStatus,
    };
    return put(`/api/proxy/ams/transaction-service/account/${accountId}/transaction/${transactionId}/status`, body);
  }

  function adjustment(
    accountId: string,
    direction: string,
    amount: number,
    description: string,
    reason: string,
    effectiveDate: Date
  ) {
    const body = {
      direction,
      effectiveDate: isAfter(this._billingDate, effectiveDate) ? effectiveDate : null,
      description,
      reason,
      amount: {
        value: amount,
        currencyCode: 'GBP',
      },
    };
    return post(`/api/proxy/ams/transaction-service/account/${accountId}/adjustment`, body);
  }

  return {
    getAccount,
    getPendingAuthorizations,
    payment,
    reverseTransaction,
    releasePayment,
    changeStatus,
    adjustment,
  };
}

export function useGetAccount({ accountId }: { accountId: string }) {
  const { getAccount } = useTransactionService();
  return useQueryWithErrorHandling({
    queryKey: ['getAccount', accountId],
    queryFn: () => getAccount(accountId),
    enabled: !!accountId,
  });
}

export function useGetSortedPendingAuthorizations({ accountId }: { accountId: string }) {
  const { getPendingAuthorizations } = useTransactionService();
  return useQueryWithErrorHandling({
    queryKey: ['getPendingAuthorizations', accountId],
    queryFn: () => getPendingAuthorizations(accountId),
    enabled: !!accountId,
    select: (data) =>
      data?.pendingAuthorizations.sort(
        (a, b) => new Date(b.authorizationDate).getTime() - new Date(a.authorizationDate).getTime()
      ),
  });
}

export function useRefreshPendingAuthorizations() {
  const { accountId } = useAccountIdContext();
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ['getPendingAuthorizations', accountId],
    });
}
